import React, { createContext, useContext, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useWhatsAppInboxSSE } from "../hooks/useWhatsAppInboxSSE";

const WhatsAppEventsContext = createContext({
  connectionState: "idle",
  stats: {
    totalMessages: 0,
    unreadMessages: 0,
    conversations: 0,
    unreadConversations: 0,
  },
  lastInboxEvent: null,
});

function formatInboxToastLabel(message) {
  const name = String(message?.schoolName || "").trim();
  const code = String(message?.schoolCode || "").trim();
  if (name && code && !name.toLowerCase().includes(code.toLowerCase())) {
    return `${name} (${code})`;
  }
  if (name) return name;
  if (code) return code;
  return message?.contactNumber || "Unknown contact";
}

export function WhatsAppEventsProvider({ children, enabled = true }) {
  const location = useLocation();

  const handleInboxMessage = useCallback(
    (message) => {
      const isInboxPage = location.pathname.includes("/whatsapp/inbox");
      const preview = String(message?.preview || message?.message || "").trim();
      const label = formatInboxToastLabel(message);

      if (!isInboxPage) {
        toast.success(
          preview ? `New WhatsApp from ${label}: ${preview}` : `New WhatsApp message from ${label}`,
          { duration: 5000, icon: "💬" }
        );
      }
    },
    [location.pathname]
  );

  const { connectionState, stats, lastInboxEvent } = useWhatsAppInboxSSE({
    enabled,
    onInboxMessage: handleInboxMessage,
  });

  const value = useMemo(
    () => ({
      connectionState,
      stats,
      lastInboxEvent,
    }),
    [connectionState, stats, lastInboxEvent]
  );

  return (
    <WhatsAppEventsContext.Provider value={value}>{children}</WhatsAppEventsContext.Provider>
  );
}

export function useWhatsAppEvents() {
  return useContext(WhatsAppEventsContext);
}

export default WhatsAppEventsContext;
